<template>
  <!-- 模板部分保持不变 -->
  <div class="custom-upload-component">
    <div class="custom-upload-component-wapper">
      <!-- 使用uid作为key更稳定 -->
      <div v-for="item in fileList" :key="item.uid" class="file-info">
        <!-- 图片类型显示图片预览 -->
        <div v-if="isImageFile(item)" class="image-preview">
          <el-image
              class="preview-image"
              :src="getImageUrl(item)"
              :preview-src-list="[getImageUrl(item)]">
          </el-image>
          <div class="image-actions">
            <el-button
              type="text"
              size="mini"
              @click.prevent="handleDownLoad(item)"
              >下载</el-button
            >
            <el-button
              type="text"
              size="mini"
              @click.prevent="handleRemove(item)"
              >删除</el-button
            >
          </div>
        </div>
        <!-- 非图片类型保留原有逻辑 -->
        <template v-else>
          <span class="file-name" :title="item.originalFilename">
            <span>{{ item.originalFilename }}</span>
          </span>
          <span class="file-size"
            >文件大小: {{ formatFileSize(item.fileSize) }}</span
          >
          <span class="upload-time"
            >上传日期: {{ formatDate(item.uploadTime || new Date()) }}</span
          >
          <!-- 上传状态显示 -->
          <div v-if="!item.fileExtension" class="file-action">
            <el-button
              type="text"
              size="mini"
              @click.prevent="handleReupload(item)"
              >重新上传</el-button
            >
            <el-button
              type="text"
              size="mini"
              @click.prevent="handleRemove(item)"
              >删除</el-button
            >
          </div>
          <div v-else>
            <el-button
              type="text"
              size="mini"
              @click.prevent="handleDownLoad(item)"
              >下载附件</el-button
            >
          </div>
        </template>
      </div>
    </div>
    <el-upload
      ref="upload"
      class="upload-demo"
      :http-request="handleCustomUpload"
      multiple
      action=""
      :accept="acceptTypes"
      :limit="limit"
      :on-exceed="handleExceed"
      list-type="text"
      :file-list="fileList"
      :on-remove="handleRemove"
      :show-file-list="false"
      :drag="true"
      :before-upload="beforeUpload"
      v-show="fileList.length < limit"
    >
      <template #default>
        <div
          class="uploadBoxs"
          :class="{ 'drag-over': isDragging }"
          @dragover.prevent="isDragging = true"
          @dragleave="isDragging = false"
          @drop.prevent="handleDrop"
        >
          <span>{{ uploadText }}</span>
        </div>
      </template>
    </el-upload>
  </div>
</template>

<script>
import { uploadImg } from '@/API/upload/upload';
import { formatTime } from '@/util/FormatData';
export default {
  name: 'ContractUploadComponent',
  props: {
    fileLists: {
      type: Array,
      default: () => {
        return [];
      },
    },
    limit: {
      type: Number,
      default: 10,
    },
    uploadText: {
      type: String,
      default: '点击上传 / 拖拽到此区域',
    },
  },
  data() {
    return {
      fileList: [],
      acceptTypes: '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.bmp,.webp',
      isDragging: false, // 拖拽状态标记
    };
  },
  watch: {
    fileLists: {
      immediate: true,
      deep: true,
      handler: function (newVal) {
        // 修复：避免直接修改props数据，创建新的数组副本
        this.fileList = [...newVal];
      },
    },
  },
  methods: {
    // 判断是否为图片文件
    isImageFile(file) {
      if (!file || !file.originalFilename) return false;
      const imageExtensions = [
        '.jpg',
        '.jpeg',
        '.png',
        '.gif',
        '.bmp',
        '.webp',
      ];
      const fileName = file.originalFilename.toLowerCase();
      return imageExtensions.some((ext) => fileName.endsWith(ext));
    },

    // 获取图片URL
    getImageUrl(file) {
      if (!file || !file.storagePath) return '';
      // 如果storagePath已经是完整URL，则直接返回
      if (file.storagePath.startsWith('http')) {
        return file.storagePath;
      }
      // 否则构造完整URL
      return process.env.VUE_APP_BASE_URL  + file.storagePath;
    },

    // 处理图片加载错误
    handleImageError(event) {
      event.target.src =
        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2NjYyIvPjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1zaXplPSIxMiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzk5OSI+5byg5bCP55qEPC90ZXh0Pjwvc3ZnPg==';
    },

    // 自定义上传逻辑
    async handleCustomUpload(options) {
      const { file, onSuccess, onError } = options;
      // 标记上传状态
      file.status = 'uploading';
      file.uid =
        file.uid || Date.now() + Math.random().toString(36).substr(2, 9);

      // 上传前记录文件大小和文件名
      const fileSize = file.size;
      const fileName = file.name;

      try {
        // 校验文件大小
        if (!this.validateFileSize(fileSize)) {
          file.status = 'error';
          onError(new Error('文件过大'));
          this.$message.error('文件大小不能超过10MB');
          return;
        }

        const formData = new FormData();
        formData.append('file', file);

        const response = await uploadImg(formData);

        // 上传成功后完善文件信息，新增size和fileName字段
        const updatedFile = {
          ...file,
          status: 'success',
          uploadTime: formatTime(new Date().getTime(), 'YYYY-MM-DD HH:mm:ss'),
          storagePath: response.data || '',
          id: new Date().getTime(),
          history: true,
          fileSize: fileSize, // 新增size字段
          originalFilename: fileName, // 新增fileName字段
        };

        // 更新文件列表，确保不修改原始数据
        this.fileList = [...this.fileList, updatedFile];
        // 向父组件抛出更新后的文件列表
        this.$emit('file-change', [...this.fileList]);
        onSuccess(response);
        this.$message.success('文件上传成功');
      } catch (error) {
        file.status = 'error';
        onError(error);
        this.$message.error('文件上传失败，请重试');
      } finally {
        this.isDragging = false; // 重置拖拽状态
      }
    },

    // 处理拖拽放下事件
    handleDrop(e) {
      this.isDragging = false;
      const files = e.dataTransfer.files;
      if (files.length) {
        // 将拖拽的文件添加到上传队列
        this.handleFiles(files);
      }
    },

    // 处理文件列表
    handleFiles(files) {
      // 转换为数组并过滤不符合条件的文件
      Array.from(files).forEach((file) => {
        // 检查文件类型
        if (!this.validateFileType(file.name)) {
          this.$message.error(`不支持的文件类型: ${file.name}`);
          return;
        }
        // 检查文件大小
        if (!this.validateFileSize(file.size)) {
          this.$message.error(`文件过大: ${file.name}（不能超过10MB）`);
          return;
        }
        // 检查是否超过数量限制
        if (this.fileList.length >= 10) {
          this.handleExceed([file], this.fileList);
          return;
        }
        // 添加上传所需的uid（模拟element-ui的处理）
        file.uid = Date.now() + Math.random().toString(36).substr(2, 9);
        // 触发上传
        this.handleCustomUpload({
          file,
          onSuccess: () => {},
          onError: () => {},
        });
      });
    },

    // 上传前校验
    beforeUpload(file) {
      // 校验文件类型
      if (!this.validateFileType(file.name)) {
        this.$message.error(
          `不支持的文件类型: ${file.name}，仅支持${this.acceptTypes}`,
        );
        return false;
      }
      // 校验文件大小
      if (!this.validateFileSize(file.size)) {
        this.$message.error(`文件过大: ${file.name}（不能超过10MB）`);
        return false;
      }
      return true;
    },

    // 校验文件类型
    validateFileType(fileName) {
      const ext = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
      return this.acceptTypes.split(',').includes(ext);
    },

    // 校验文件大小（限制10MB）
    validateFileSize(size) {
      const maxSize = 10 * 1024 * 1024; // 10MB
      return size <= maxSize;
    },

    handleExceed(files, fileList) {
      this.$message.warning(`最多只能上传 10 个文件`);
    },

    handleRemove(file) {
      const index = this.fileList.findIndex((item) => item.uid === file.uid);
      if (index !== -1) {
        // 创建新的数组而不是直接修改原数组
        this.fileList = this.fileList.filter((_, i) => i !== index);
        // 向父组件抛出更新后的文件列表
        this.$emit('file-change', [...this.fileList]);
      }
    },
    handleDownLoad(file) {
      console.log(file);
      // 检查文件信息是否完整
      if (!file || !file.storagePath) {
        this.$message.error('文件信息不完整，无法下载');
        return;
      }

      // 构建完整下载地址
      const downloadUrl = process.env.VUE_APP_BASE_URL + file.storagePath;
      console.log(downloadUrl, '打印附件下载地址');
      // 在新窗口中打开文件链接
      window.open(downloadUrl, '_blank');
      // 提示用户操作结果
      this.$message.success(`开始下载：${file.originalFilename}`);
    },
    handleReupload(file) {
      this.handleRemove(file);
      // 触发重新选择文件
      this.$refs.upload.$el.querySelector('input').click();
    },

    formatFileSize(size) {
      // 处理异常情况
      if (
        size === undefined ||
        size === null ||
        typeof size !== 'number' ||
        isNaN(size)
      ) {
        return '未知大小';
      }

      if (size < 1024) return size + ' Bytes';
      if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB';
      return (size / (1024 * 1024)).toFixed(1) + ' MB';
    },

    formatDate(date) {
      // 增加日期格式化容错处理
      if (!(date instanceof Date)) {
        date = new Date(date);
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
  },
};
</script>

<style scoped>
/* 样式部分保持不变 */
.custom-upload-component {
  display: flex;
  flex-wrap: wrap;
}
.custom-upload-component-wapper {
  display: flex;
  /*max-width: calc(100% - 240px);*/
}
.upload-demo .el-upload--text {
  border: 1px dashed #dcdcdc;
  border-radius: 6px;
  padding: 40px 20px;
  width: 175px;
  text-align: center;
}
.file-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  width: 175px;
  height: 140px;
  margin-right: 20px;
}
.image-preview {
  display: flex;
  flex-direction: column;
  width: 155px!important;
  height: 120px!important;
  align-items: center;
}
.preview-image {
  width: 135px!important;
  height: 100px!important;
}
.image-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 5px;
}
.file-name {
  font-weight: bold;
  word-break: break-all;
}
.file-name span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.file-size,
.upload-time {
  font-size: 12px;
  padding: 5px 0;
  color: #666;
}
.file-action {
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 5px;
}
.uploadBoxs {
  width: 175px;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #dcdcdc;
  transition: all 0.3s;
}
/* 拖拽悬停样式 */
.uploadBoxs.drag-over {
  border-color: #409eff;
  background-color: rgba(64, 158, 255, 0.1);
}
/* 上传状态样式 */
.upload-status {
  font-size: 12px;
}
.upload-status.success {
  color: #67c23a;
}
.upload-status.error {
  color: #f56c6c;
}
/* 隐藏默认上传列表 */
::v-deep .el-upload-list {
  display: none !important;
}
::v-deep .el-upload-dragger {
  width: 175px !important;
  height: 140px !important;
}
</style>
